import { scan as scanTemplates } from './templatePreview';
// Definición de una herramienta en el admin. Debe coincidir con el motor (agent-runtime/src/tools/definition.ts): el motor valida a
// fondo al probar y al ejecutar; aquí hay valores por defecto, nombres y ayudas para el editor.

export const NODE_TYPES = {
  supabase: { label: 'Supabase', hint: 'Guardar, buscar o actualizar filas, o llamar una función', accent: '#3ECF8E' },
  http: { label: 'Llamar una API', hint: 'Petición https a un servicio externo', accent: '#F5A524' },
  code: { label: 'Código', hint: 'Transformar datos con JavaScript y dejar el resultado para otros pasos', accent: '#06B6D4' },
  set: { label: 'Armar datos', hint: 'Juntar o renombrar datos para el siguiente paso', accent: '#8B5CF6' },
  sheets: { label: 'Google Sheets', hint: 'Agregar una fila, buscar filas o actualizar una fila', accent: '#0F9D58' },
  if: { label: 'Si / no', hint: 'Seguir un camino u otro según una condición', accent: '#EC4899' },
  respond: { label: 'Respuesta al agente', hint: 'Lo que el agente recibe como resultado', accent: '#2BD9FE' },
};
// Qué tipo de conector usa cada paso. El paso de API solo usa conector si su identificación es «Conector» (ver usesConnector).
export const NODE_CONNECTOR = { supabase: 'supabase', http: 'http', sheets: 'gsheets' };
/** Cómo se identifica un paso de API. Un paso viejo (con conector y ruta, sin `auth`) se identifica con su conector. */
export const httpAuthType = (n) => n.auth?.type ?? (n.url === undefined ? 'connector' : 'none');
export const usesConnector = (n) => (n.type === 'http' ? httpAuthType(n) === 'connector' : Boolean(NODE_CONNECTOR[n.type]));
export const HTTP_AUTHS = [
  { key: 'none', label: 'Ninguna', hint: 'API abierta' },
  { key: 'bearer', label: 'Token (Bearer)', hint: 'Authorization: Bearer …' },
  { key: 'basic', label: 'Usuario y contraseña', hint: 'Authorization: Basic …' },
  { key: 'header', label: 'Clave en un encabezado', hint: 'Por ejemplo X-Api-Key' },
  { key: 'connector', label: 'Conector (clave guardada)', hint: 'La forma más segura: la clave no queda en la herramienta' },
];
export const HTTP_BODY_MODES = [
  { key: 'none', label: 'Sin cuerpo' }, { key: 'fields', label: 'Campos' }, { key: 'json', label: 'JSON' }, { key: 'text', label: 'Texto' },
];
export const VAR_TYPES = [
  { key: 'auto', label: 'Tal como llega' }, { key: 'string', label: 'Texto' }, { key: 'number', label: 'Número' }, { key: 'integer', label: 'Número entero' },
  { key: 'boolean', label: 'Sí o no' }, { key: 'json', label: 'JSON (objeto o lista)' },
];
export const CONNECTOR_KINDS = { supabase: 'Supabase', http: 'API', gsheets: 'Google Sheets' };
export const COND_OPS = [
  { key: 'eq', label: 'es igual a' }, { key: 'neq', label: 'no es igual a' }, { key: 'contains', label: 'contiene' }, { key: 'not_contains', label: 'no contiene' },
  { key: 'gt', label: 'es mayor que' }, { key: 'gte', label: 'es mayor o igual a' }, { key: 'lt', label: 'es menor que' }, { key: 'lte', label: 'es menor o igual a' },
  { key: 'empty', label: 'está vacío' }, { key: 'not_empty', label: 'no está vacío' },
];
/** Métodos de las plantillas ({{dato | metodo}}). Deben coincidir con PIPES de agent-runtime/src/tools/template.ts. */
export const METHODS = [
  { name: 'mayusculas', label: 'En mayúsculas', example: 'ana → ANA' },
  { name: 'minusculas', label: 'En minúsculas', example: 'ANA → ana' },
  { name: 'recortar', label: 'Quitar espacios de las orillas', example: '" ana " → "ana"' },
  { name: 'limite', arg: '100', label: 'Cortar a N caracteres', example: 'textos largos' },
  { name: 'defecto', arg: '"sin dato"', label: 'Valor si está vacío', example: 'vacío → "sin dato"' },
  { name: 'cantidad', label: 'Cuántos hay', example: 'filas → 3' },
  { name: 'primero', label: 'El primero de la lista' },
  { name: 'ultimo', label: 'El último de la lista' },
  { name: 'campo', arg: '"nombre"', label: 'Un campo (de cada fila)', example: 'filas → nombres' },
  { name: 'unir', arg: '", "', label: 'Unir una lista en texto', example: '[a, b] → "a, b"' },
  { name: 'numero', label: 'Como número', example: '"12" → 12' },
  { name: 'redondear', arg: '2', label: 'Redondear a N decimales' },
  { name: 'moneda', label: 'Como pesos', example: '1234.5 → $1,234.50' },
  { name: 'fecha', arg: '"completa"', label: 'Fecha (corta, larga, completa u hora)', example: '9 de octubre de 2026 15:30' },
  { name: 'existe', label: '¿Tiene valor? (sí/no)' },
  { name: 'json', label: 'Como JSON' },
  { name: 'texto', label: 'Como texto' },
];
const METHOD_NAMES = new Set(METHODS.map((m) => m.name));
/** Métodos que no existen en un texto con plantillas. */
export function unknownMethods(text) {
  const bad = [];
  for (const t of scanTemplates(String(text || ''))) {
    if (t.expr.startsWith('=')) continue;   // código: no lleva métodos
    for (const p of t.expr.split('|').slice(1)) { const n = p.split(':')[0].trim(); if (n && !METHOD_NAMES.has(n)) bad.push(n); }
  }
  return bad;
}

export const SHEETS_OPS = [
  { key: 'append', label: 'Agregar una fila' }, { key: 'find', label: 'Buscar filas' }, { key: 'update', label: 'Actualizar una fila' },
];
export const INPUT_SOURCES = [
  { key: 'ai', label: 'Lo saca la IA', hint: 'De la conversación, con tu descripción' },
  { key: 'fixed', label: 'Valor fijo', hint: 'Siempre el mismo; la IA no lo ve' },
  { key: 'context', label: 'De la conversación', hint: 'Lo pone el sistema; la IA no lo puede cambiar' },
];
export const INPUT_TYPES = [
  { key: 'string', label: 'Texto' }, { key: 'integer', label: 'Número entero' }, { key: 'number', label: 'Número' },
  { key: 'boolean', label: 'Sí o no' }, { key: 'enum', label: 'Una de varias opciones' },
];
export const CONTEXT_KEYS = [
  { key: 'phone', label: 'Teléfono (WhatsApp)' }, { key: 'name', label: 'Nombre en el canal' }, { key: 'chat_id', label: 'Id de la conversación' },
  { key: 'channel', label: 'Canal' }, { key: 'contact_id', label: 'Id del contacto' }, { key: 'project_id', label: 'Id del proyecto' },
  { key: 'agent_id', label: 'Id del agente' }, { key: 'now', label: 'Fecha y hora (ISO)' }, { key: 'date', label: 'Fecha de hoy' },
];
export const SUPABASE_OPS = [
  { key: 'insert', label: 'Insertar una fila' }, { key: 'upsert', label: 'Insertar o actualizar si ya existe' },
  { key: 'select', label: 'Buscar filas' }, { key: 'update', label: 'Actualizar filas' }, { key: 'rpc', label: 'Llamar una función (RPC)' },
];
export const FILTER_OPS = [
  { key: 'eq', label: 'es igual a' }, { key: 'neq', label: 'no es' }, { key: 'gt', label: 'mayor que' }, { key: 'gte', label: 'mayor o igual' },
  { key: 'lt', label: 'menor que' }, { key: 'lte', label: 'menor o igual' }, { key: 'ilike', label: 'se parece a (con %)' }, { key: 'is', label: 'es (null/true/false)' },
];

/** «Registrar lead» → «registrar_lead»: el nombre que ve el modelo. */
export function slugify(name) {
  const s = String(name || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 48);
  return /^[a-z]/.test(s) ? s : s ? `h_${s}`.slice(0, 48) : '';
}
export const validSlug = (s) => /^[a-z][a-z0-9_]{1,47}$/.test(s) && s !== 'request_human_help';

export const emptyDraft = () => ({
  description: '', instructions: '',
  inputs: [],
  connectors: [],
  nodes: [{ id: 'respuesta', type: 'respond', name: 'Respuesta al agente', text: '' }],
});

let seq = 0;
export function newNode(type, existing) {
  const base = { supabase: 'guardar', http: 'llamar', code: 'codigo', set: 'datos', respond: 'respuesta', sheets: 'hoja', if: 'decidir' }[type];
  const used = new Set(allNodes(existing).map((n) => n.id));
  let id = base; while (used.has(id)) id = `${base}_${++seq}`;
  const name = NODE_TYPES[type].label;
  if (type === 'supabase') return { id, type, name, slot: '', operation: 'insert', table: '', values: [], filters: [], on_conflict: '', columns: '', limit: 20 };
  if (type === 'http') return { id, type, name, method: 'GET', url: 'https://', auth: { type: 'none' }, query: [], headers: [], body: [], body_mode: 'none', body_raw: '' };
  if (type === 'code') return { id, type, name, vars: [], code: '// Lo que devuelvas con return lo pueden usar los pasos siguientes.\nreturn {};' };
  if (type === 'sheets') return { id, type, name, slot: '', operation: 'append', spreadsheet: '', sheet: '', values: [], match: { column: '', value: '' }, limit: 20 };
  if (type === 'if') return { id, type, name, match: 'all', conditions: [{ left: '', op: 'eq', right: '' }], then: [], else: [] };
  if (type === 'set') return { id, type, name, fields: [] };
  return { id, type, name, text: '' };
}

// ── Árbol de pasos: una lista se identifica por su dirección: [] es la principal; [idDelSiNo, 'then'|'else'] una rama, y así hacia abajo. ──
export const allNodes = (list = []) => list.flatMap((n) => (n.type === 'if' ? [n, ...allNodes(n.then), ...allNodes(n.else)] : [n]));
export function getList(nodes, addr) {
  let list = nodes;
  for (let i = 0; i < addr.length; i += 2) list = (list.find((n) => n.id === addr[i])?.[addr[i + 1]]) || [];
  return list;
}
function mapList(nodes, addr, fn) {
  if (!addr.length) return fn(nodes);
  const [id, br, ...rest] = addr;
  return nodes.map((n) => (n.id === id ? { ...n, [br]: mapList(n[br] || [], rest, fn) } : n));
}
/** Dónde está un paso: { addr, index, node }. */
export function locate(nodes, id, addr = []) {
  for (let i = 0; i < nodes.length; i++) {
    const n = nodes[i];
    if (n.id === id) return { addr, index: i, node: n };
    if (n.type === 'if') for (const br of ['then', 'else']) { const r = locate(n[br] || [], id, [...addr, n.id, br]); if (r) return r; }
  }
  return null;
}
export const updateNode = (nodes, id, patch) => nodes.map((n) => (n.id === id ? { ...n, ...patch } : n.type === 'if' ? { ...n, then: updateNode(n.then || [], id, patch), else: updateNode(n.else || [], id, patch) } : n));
export const removeNode = (nodes, id) => nodes.filter((n) => n.id !== id).map((n) => (n.type === 'if' ? { ...n, then: removeNode(n.then || [], id), else: removeNode(n.else || [], id) } : n));
export function moveNode(nodes, id, dir) {
  const at = locate(nodes, id); if (!at) return nodes;
  return mapList(nodes, at.addr, (list) => {
    const j = at.index + dir; if (j < 0 || j >= list.length) return list;
    const next = [...list]; [next[at.index], next[j]] = [next[j], next[at.index]]; return next;
  });
}
/** Inserta en una lista; la respuesta al agente siempre queda al final de su lista. */
export function insertNode(nodes, addr, index, node) {
  return mapList(nodes, addr, (list) => {
    let at = Math.min(index, list.length);
    if (node.type !== 'respond' && at === list.length && list[list.length - 1]?.type === 'respond') at = list.length - 1;
    return [...list.slice(0, at), node, ...list.slice(at)];
  });
}
export const canAddRespond = (nodes, addr) => !getList(nodes, addr).some((n) => n.type === 'respond');
export const depthOf = (addr) => addr.length / 2;

/** Pasos que ya corrieron cuando llega el turno de `id` (los de ramas anteriores, marcados porque puede que no hayan corrido). */
function before(list, id) {
  for (let i = 0; i < list.length; i++) {
    const n = list[i];
    if (n.id === id) return { found: true, nodes: list.slice(0, i) };
    if (n.type === 'if') for (const br of ['then', 'else']) {
      const r = before(n[br] || [], id);
      if (r.found) return { found: true, nodes: [...list.slice(0, i), n, ...r.nodes] };
    }
  }
  return { found: false, nodes: [] };
}

/** Pasos que pueden haber corrido antes de `id` (los de ramas anteriores con `maybe: true`). */
export function previousNodes(nodes, id) {
  return before(nodes || [], id).nodes.flatMap((n) => (n.type === 'if' ? [n, ...allNodes(n.then).map((x) => ({ ...x, maybe: true })), ...allNodes(n.else).map((x) => ({ ...x, maybe: true }))] : [n]));
}

/** Datos que se pueden usar en un paso: las entradas y lo que devolvieron los pasos anteriores. */
export function variablesFor(draft, nodeId) {
  const vars = (draft.inputs || []).map((i) => ({ path: `entrada.${i.key}`, label: i.key, group: 'Entrada' }));
  const prev = before(draft.nodes || [], nodeId).nodes;
  const flat = prev.flatMap((n) => (n.type === 'if' ? [n, ...allNodes(n.then).map((x) => ({ ...x, maybe: true })), ...allNodes(n.else).map((x) => ({ ...x, maybe: true }))] : [n]));
  for (const n of flat) {
    const g = n.maybe ? 'Pasos de una rama (puede que no hayan corrido)' : 'Pasos';
    if (n.type === 'set') for (const f of n.fields || []) vars.push({ path: `nodos.${n.id}.${f.key}`, label: `${n.name} · ${f.key}`, group: g });
    else if (n.type === 'supabase') vars.push({ path: `nodos.${n.id}.data.0.id`, label: `${n.name} · id de la primera fila`, group: g }, { path: `nodos.${n.id}.data`, label: `${n.name} · todas las filas`, group: g });
    else if (n.type === 'code') vars.push({ path: `nodos.${n.id}`, label: `${n.name} · resultado del código`, group: g });
    else if (n.type === 'http') vars.push({ path: `nodos.${n.id}.data`, label: `${n.name} · respuesta`, group: g }, { path: `nodos.${n.id}.status`, label: `${n.name} · código`, group: g });
    else if (n.type === 'sheets') {
      if (n.operation === 'append') vars.push({ path: `nodos.${n.id}.data.fila`, label: `${n.name} · número de fila`, group: g });
      else if (n.operation === 'find') vars.push({ path: `nodos.${n.id}.data`, label: `${n.name} · filas encontradas`, group: g }, { path: `nodos.${n.id}.data.0.fila`, label: `${n.name} · fila de la primera`, group: g });
      else vars.push({ path: `nodos.${n.id}.data.actualizada`, label: `${n.name} · ¿se actualizó?`, group: g });
    } else if (n.type === 'if') vars.push({ path: `nodos.${n.id}.resultado`, label: `${n.name} · ¿se cumplió?`, group: g });
  }
  return vars;
}

const CODE_RESERVED = new Set(('break case catch class const continue debugger default delete do else enum export extends false finally for function if import in instanceof let new null return super switch this throw true try typeof var void while with yield await async static of arguments eval undefined NaN Infinity globalThis '
  + 'JSON Math Date Object Array String Number Boolean Promise Symbol Map Set RegExp Error parseInt parseFloat isNaN isFinite fechaMx formatoFecha sumarDias pesos entrada nodos contacto datos mensaje ahora agente').split(' '));

/** Revisión rápida para el editor (el motor tiene la completa). Devuelve avisos por paso: { [nodeId|'_tool']: [mensajes] }. */
export function quickCheck(draft) {
  const out = {};
  const add = (k, m) => { (out[k] = out[k] || []).push(m); };
  if ((draft.description || '').trim().length < 10) add('_tool', 'Falta la descripción: para qué sirve y cuándo usarla.');
  for (const i of draft.inputs || []) {
    if (!/^[a-z][a-z0-9_]{0,39}$/.test(i.key || '')) add('_tool', `El dato «${i.key || '(sin nombre)'}» lleva solo minúsculas, números y _.`);
    if (i.source === 'ai' && !(i.description || '').trim()) add('_tool', `Explica qué va en «${i.key}».`);
  }
  for (const s of draft.connectors || []) if (s.mode === 'fixed' && !s.connector_id) add('_tool', `Elige el conector de «${s.label || s.slot}».`);
  const check = (list, depth) => list.forEach((n, idx) => {
    if (usesConnector(n) && !n.slot) add(n.id, 'Elige su conector.');
    if (n.type === 'http') {
      const at = httpAuthType(n);
      if (at !== 'connector' && !/^(\{\{|https:\/\/\S)/i.test((n.url || '').trim())) add(n.id, 'La dirección debe empezar con https://.');
      if (at === 'bearer' && !(n.auth?.token || '').trim()) add(n.id, 'Escribe el token (o elige un dato).');
      if (at === 'basic' && !(n.auth?.user || '').trim()) add(n.id, 'Escribe el usuario.');
      if (at === 'header' && (!(n.auth?.name || '').trim() || !(n.auth?.value || '').trim())) add(n.id, 'Escribe el nombre del encabezado y el valor de la clave.');
      if (n.method === 'GET' && (n.body_mode || 'none') !== 'none') add(n.id, 'GET no lleva cuerpo: pon los datos en los parámetros.');
    }
    if (n.type === 'code') {
      if (!(n.code || '').trim()) add(n.id, 'Escribe el código.');
      if ((n.code || '').length > 8000) add(n.id, 'El código es demasiado largo (máximo 8000 caracteres).');
      const seenVars = new Set();
      for (const v of n.vars || []) {
        if (!/^[A-Za-z_][A-Za-z0-9_]{0,39}$/.test(v.key || '') || /^__/.test(v.key) || CODE_RESERVED.has(v.key)) add(n.id, `«${v.key || '(sin nombre)'}» no sirve como nombre de variable.`);
        else if (seenVars.has(v.key)) add(n.id, `La variable «${v.key}» está repetida.`);
        seenVars.add(v.key);
      }
    }
    if (n.type === 'supabase' && n.operation !== 'rpc' && !n.table) add(n.id, 'Elige la tabla.');
    if (n.type === 'supabase' && n.operation === 'rpc' && !n.fn) add(n.id, 'Elige la función.');
    if (n.type === 'supabase' && n.operation === 'update' && !(n.filters || []).length) add(n.id, 'Actualizar necesita al menos un filtro.');
    if (n.type === 'sheets' && !/[A-Za-z0-9_-]{20,}/.test(n.spreadsheet || '')) add(n.id, 'Pega el enlace de la hoja de cálculo.');
    if (n.type === 'sheets' && !n.sheet) add(n.id, 'Elige la pestaña.');
    if (n.type === 'sheets' && n.operation !== 'append' && !n.match?.column) add(n.id, 'Elige la columna para buscar la fila.');
    if (n.type === 'sheets' && n.operation !== 'find' && !(n.values || []).length) add(n.id, 'Agrega al menos una columna con su valor.');
    if (n.type === 'if' && (!(n.conditions || []).length || n.conditions.some((c) => !(c.left || '').trim()))) add(n.id, 'Completa las condiciones (el dato a revisar).');
    if (n.type === 'if' && depth >= 3) add(n.id, 'Como máximo 3 «Si / no» uno dentro de otro.');
    if (n.type === 'respond' && idx !== list.length - 1) add(n.id, 'La respuesta debe ser el último paso de su camino.');
    if (n.type === 'respond' && !(n.text || '').trim()) add(n.id, 'Escribe lo que recibe el agente (usa { } para poner datos de los pasos).');
    const bad = unknownMethods(JSON.stringify(n.type === 'if' ? n.conditions : n));
    if (bad.length) add(n.id, `Método desconocido: ${[...new Set(bad)].join(', ')}.`);
    if (n.type === 'if') { check(n.then || [], depth + 1); check(n.else || [], depth + 1); }
  });
  const nodes = draft.nodes || [];
  check(nodes, 0);
  if (!nodes.length) add('_tool', 'Agrega al menos un paso.');
  if (allNodes(nodes).length > 20) add('_tool', 'Máximo 20 pasos por herramienta (contando los de las ramas).');
  return out;
}

/**
 * Rutas usables a partir de un JSON de muestra (la salida de un paso en la última prueba): `nodos.<id>.data.0.nombre`, etc.
 * De las listas se toma el primer elemento (y la lista completa); hasta 4 niveles y 60 rutas.
 */
export function pathsFromSample(base, value, label, max = 60) {
  const out = [];
  const walk = (v, path, depth) => {
    if (out.length >= max) return;
    const sample = v === null || typeof v !== 'object' ? JSON.stringify(v)?.slice(0, 40) : Array.isArray(v) ? `lista de ${v.length}` : 'objeto';
    if (path !== base) out.push({ path, label: `${label} · ${path.slice(base.length + 1)}`, hint: sample });
    if (depth >= 4 || v === null || typeof v !== 'object') return;
    if (Array.isArray(v)) { if (v.length && typeof v[0] === 'object') walk(v[0], `${path}.0`, depth + 1); else if (v.length) out.push({ path: `${path}.0`, label: `${label} · ${path.slice(base.length + 1)}.0`, hint: JSON.stringify(v[0])?.slice(0, 40) }); return; }
    for (const [k, x] of Object.entries(v)) if (/^[A-Za-z0-9_-]+$/.test(k)) walk(x, `${path}.${k}`, depth + 1);
  };
  walk(value, base, 0);
  return out;
}
